import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { adminApi, categoriesApi } from '../services/api';
import { Pencil, Trash2, Search, Eye, Star } from 'lucide-react';

const OWNERSHIP_TAGS = [
  'Woman-Owned', 'Veteran-Owned', 'Black-Owned', 'Hispanic/Latino-Owned',
  'Asian-Owned', 'Native American-Owned', 'LGBTQ+-Owned', 'Disability-Owned'
];

export const AdminAddBusiness = ({ onSuccess, editData, onCancel }) => {
  const isEdit = !!editData;
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    business_name: '', primary_category: '', subcategory: '', description: '',
    street_address: '', city_state_zip: '', phone: '', website: '',
    contact_email: '', facebook: '', instagram: '', twitter: '', ownership_tags: []
  });

  useEffect(() => {
    categoriesApi.getBusinessCategories().then(res => setCategories(res.data));
  }, []);

  useEffect(() => {
    if (editData) {
      setForm({
        business_name: editData.business_name || '',
        primary_category: editData.primary_category || '',
        subcategory: editData.subcategory || '',
        description: editData.description || '',
        street_address: editData.street_address || '',
        city_state_zip: editData.city_state_zip || '',
        phone: editData.phone || '',
        website: editData.website || '',
        contact_email: editData.contact_email || '',
        facebook: editData.facebook || '',
        instagram: editData.instagram || '',
        twitter: editData.twitter || '',
        ownership_tags: editData.ownership_tags || []
      });
      if (editData.primary_category) {
        categoriesApi.getBusinessSubcategories(editData.primary_category).then(res => setSubcategories(res.data));
      }
    }
  }, [editData]);

  const handleCategoryChange = async (cat) => {
    setForm({ ...form, primary_category: cat, subcategory: '' });
    if (cat) {
      const res = await categoriesApi.getBusinessSubcategories(cat);
      setSubcategories(res.data);
    } else { setSubcategories([]); }
  };

  const toggleTag = (tag) => {
    setForm(prev => ({
      ...prev,
      ownership_tags: prev.ownership_tags.includes(tag)
        ? prev.ownership_tags.filter(t => t !== tag)
        : [...prev.ownership_tags, tag]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('business_name', form.business_name);
      formData.append('primary_category', form.primary_category);
      formData.append('description', form.description);
      formData.append('street_address', form.street_address);
      formData.append('city_state_zip', form.city_state_zip);
      if (form.subcategory) formData.append('subcategory', form.subcategory);
      if (form.phone) formData.append('phone', form.phone);
      if (form.website) formData.append('website', form.website);
      if (form.contact_email) formData.append('contact_email', form.contact_email);
      if (form.facebook) formData.append('facebook', form.facebook);
      if (form.instagram) formData.append('instagram', form.instagram);
      if (form.twitter) formData.append('twitter', form.twitter);
      if (form.ownership_tags.length) formData.append('ownership_tags', JSON.stringify(form.ownership_tags));
      photos.forEach(p => formData.append('photos', p));

      if (isEdit) {
        await adminApi.editBusiness(editData.id, formData);
        toast.success('Business updated!');
      } else {
        await adminApi.createBusiness(formData);
        toast.success('Business created and approved!');
      }
      if (!isEdit) {
        setForm({ business_name: '', primary_category: '', subcategory: '', description: '',
          street_address: '', city_state_zip: '', phone: '', website: '',
          contact_email: '', facebook: '', instagram: '', twitter: '', ownership_tags: [] });
        setPhotos([]);
      }
      if (onSuccess) onSuccess();
      if (isEdit && onCancel) onCancel();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${isEdit ? 'update' : 'create'} business`);
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="admin-add-business">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-white">{isEdit ? 'Edit Business' : 'Add New Business'}</h2>
        {isEdit && (
          <button onClick={onCancel} className="px-4 py-2 bg-neutral-700 text-neutral-300 rounded-lg hover:bg-neutral-600">Cancel</button>
        )}
      </div>
      <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl">
        <div className="bg-neutral-800 rounded-xl p-6 space-y-4">
          <h3 className="text-lg font-semibold text-white mb-2">Basic Info</h3>
          <div>
            <label className="block text-sm text-neutral-400 mb-1">Business Name *</label>
            <input required value={form.business_name} onChange={e => setForm({...form, business_name: e.target.value})}
              className="w-full px-4 py-2 rounded-lg bg-neutral-700 text-white border border-neutral-600 focus:border-accent outline-none" data-testid="admin-biz-name" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-neutral-400 mb-1">Category *</label>
              <select required value={form.primary_category} onChange={e => handleCategoryChange(e.target.value)}
                className="w-full px-4 py-2 rounded-lg bg-neutral-700 text-white border border-neutral-600 focus:border-accent outline-none" data-testid="admin-biz-category">
                <option value="">Select category</option>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm text-neutral-400 mb-1">Subcategory</label>
              <select value={form.subcategory} onChange={e => setForm({...form, subcategory: e.target.value})}
                disabled={!subcategories.length}
                className="w-full px-4 py-2 rounded-lg bg-neutral-700 text-white border border-neutral-600 focus:border-accent outline-none disabled:opacity-50" data-testid="admin-biz-subcategory">
                <option value="">Select subcategory</option>
                {subcategories.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm text-neutral-400 mb-1">Description * (max 300 chars)</label>
            <textarea required maxLength={300} value={form.description} onChange={e => setForm({...form, description: e.target.value})}
              className="w-full px-4 py-2 rounded-lg bg-neutral-700 text-white border border-neutral-600 focus:border-accent outline-none h-24 resize-none" data-testid="admin-biz-desc" />
            <p className="text-xs text-neutral-500 mt-1">{form.description.length}/300</p>
          </div>
        </div>

        <div className="bg-neutral-800 rounded-xl p-6 space-y-4">
          <h3 className="text-lg font-semibold text-white mb-2">Location & Contact</h3>
          <div>
            <label className="block text-sm text-neutral-400 mb-1">Street Address *</label>
            <input required value={form.street_address} onChange={e => setForm({...form, street_address: e.target.value})}
              className="w-full px-4 py-2 rounded-lg bg-neutral-700 text-white border border-neutral-600 focus:border-accent outline-none" data-testid="admin-biz-address" />
          </div>
          <div>
            <label className="block text-sm text-neutral-400 mb-1">City, State, Zip *</label>
            <input required value={form.city_state_zip} onChange={e => setForm({...form, city_state_zip: e.target.value})}
              placeholder="Martinsville, VA 24112" className="w-full px-4 py-2 rounded-lg bg-neutral-700 text-white border border-neutral-600 focus:border-accent outline-none" data-testid="admin-biz-citystatezip" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-neutral-400 mb-1">Phone</label>
              <input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
                className="w-full px-4 py-2 rounded-lg bg-neutral-700 text-white border border-neutral-600 focus:border-accent outline-none" />
            </div>
            <div>
              <label className="block text-sm text-neutral-400 mb-1">Email</label>
              <input type="email" value={form.contact_email} onChange={e => setForm({...form, contact_email: e.target.value})}
                className="w-full px-4 py-2 rounded-lg bg-neutral-700 text-white border border-neutral-600 focus:border-accent outline-none" />
            </div>
          </div>
          <div>
            <label className="block text-sm text-neutral-400 mb-1">Website</label>
            <input value={form.website} onChange={e => setForm({...form, website: e.target.value})}
              placeholder="https://" className="w-full px-4 py-2 rounded-lg bg-neutral-700 text-white border border-neutral-600 focus:border-accent outline-none" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-neutral-400 mb-1">Facebook</label>
              <input value={form.facebook} onChange={e => setForm({...form, facebook: e.target.value})}
                className="w-full px-4 py-2 rounded-lg bg-neutral-700 text-white border border-neutral-600 focus:border-accent outline-none" />
            </div>
            <div>
              <label className="block text-sm text-neutral-400 mb-1">Instagram</label>
              <input value={form.instagram} onChange={e => setForm({...form, instagram: e.target.value})}
                className="w-full px-4 py-2 rounded-lg bg-neutral-700 text-white border border-neutral-600 focus:border-accent outline-none" />
            </div>
            <div>
              <label className="block text-sm text-neutral-400 mb-1">Twitter</label>
              <input value={form.twitter} onChange={e => setForm({...form, twitter: e.target.value})}
                className="w-full px-4 py-2 rounded-lg bg-neutral-700 text-white border border-neutral-600 focus:border-accent outline-none" />
            </div>
          </div>
        </div>

        <div className="bg-neutral-800 rounded-xl p-6 space-y-4">
          <h3 className="text-lg font-semibold text-white mb-2">Ownership Tags</h3>
          <div className="flex flex-wrap gap-2">
            {OWNERSHIP_TAGS.map(tag => (
              <button key={tag} type="button" onClick={() => toggleTag(tag)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  form.ownership_tags.includes(tag) ? 'bg-accent text-neutral-900' : 'bg-neutral-700 text-neutral-300 hover:bg-neutral-600'
                }`}>{tag}</button>
            ))}
          </div>
        </div>

        <div className="bg-neutral-800 rounded-xl p-6 space-y-4">
          <h3 className="text-lg font-semibold text-white mb-2">Photos (up to 5)</h3>
          {isEdit && editData?.photos?.length > 0 && photos.length === 0 && (
            <div>
              <div className="flex gap-2 flex-wrap mb-2">
                {editData.photos.map((p, i) => (
                  <img key={i} src={`${window.location.origin}${p}`} alt="" className="w-16 h-16 object-cover rounded-lg border border-neutral-600" />
                ))}
              </div>
              <p className="text-xs text-neutral-500">Upload new photos to replace existing ones</p>
            </div>
          )}
          {photos.length > 0 && (
            <div className="flex gap-3 flex-wrap">
              {photos.map((file, i) => (
                <div key={i} className="relative group">
                  <img src={URL.createObjectURL(file)} alt="" className="w-20 h-20 object-cover rounded-lg border border-neutral-600" />
                  <button type="button" onClick={() => setPhotos(prev => prev.filter((_, idx) => idx !== i))}
                    className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    x
                  </button>
                </div>
              ))}
            </div>
          )}
          {photos.length < 5 && (
            <label className="inline-flex items-center gap-2 px-4 py-2 bg-neutral-700 text-neutral-300 rounded-lg cursor-pointer hover:bg-neutral-600 transition-colors text-sm">
              + Add Photo{photos.length > 0 ? ` (${5 - photos.length} remaining)` : 's'}
              <input type="file" accept="image/*" className="hidden" data-testid="admin-biz-photos"
                onChange={e => {
                  const newFiles = Array.from(e.target.files);
                  setPhotos(prev => [...prev, ...newFiles].slice(0, 5));
                  e.target.value = '';
                }} />
            </label>
          )}
          <p className="text-xs text-neutral-500">{photos.length}/5 photos selected</p>
        </div>

        <button type="submit" disabled={loading}
          className="px-8 py-3 bg-accent text-neutral-900 font-bold rounded-lg hover:bg-accent/90 transition-colors disabled:opacity-50"
          data-testid="admin-biz-submit">
          {loading ? (isEdit ? 'Updating...' : 'Creating...') : (isEdit ? 'Update Business' : 'Create & Approve Business')}
        </button>
      </form>
    </div>
  );
};

export const AdminAddEvent = ({ onSuccess, editData, onCancel }) => {
  const isEdit = !!editData;
  const [eventCategories, setEventCategories] = useState([]);
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    event_name: '', category: '', description: '', event_date: '',
    start_time: '', end_time: '', location: '', ticket_url: '',
    ticket_price: '', organizer: ''
  });

  useEffect(() => {
    categoriesApi.getEventCategories().then(res => setEventCategories(res.data));
  }, []);

  useEffect(() => {
    if (editData) {
      setForm({
        event_name: editData.event_name || '',
        category: editData.category || '',
        description: editData.description || '',
        event_date: editData.event_date || '',
        start_time: editData.start_time || '',
        end_time: editData.end_time || '',
        location: editData.location || '',
        ticket_url: editData.ticket_url || '',
        ticket_price: editData.cost || editData.ticket_price || '',
        organizer: editData.organization_name || editData.organizer || ''
      });
    }
  }, [editData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, val]) => { if (val) formData.append(key, val); });
      if (image) formData.append('image', image);

      if (isEdit) {
        await adminApi.editEvent(editData.id, formData);
        toast.success('Event updated!');
      } else {
        await adminApi.createEvent(formData);
        toast.success('Event created and approved!');
      }
      if (!isEdit) {
        setForm({ event_name: '', category: '', description: '', event_date: '',
          start_time: '', end_time: '', location: '', ticket_url: '', ticket_price: '', organizer: '' });
        setImage(null);
      }
      if (onSuccess) onSuccess();
      if (isEdit && onCancel) onCancel();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${isEdit ? 'update' : 'create'} event`);
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="admin-add-event">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-white">{isEdit ? 'Edit Event' : 'Add New Event'}</h2>
        {isEdit && (
          <button onClick={onCancel} className="px-4 py-2 bg-neutral-700 text-neutral-300 rounded-lg hover:bg-neutral-600">Cancel</button>
        )}
      </div>
      <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl">
        <div className="bg-neutral-800 rounded-xl p-6 space-y-4">
          <h3 className="text-lg font-semibold text-white mb-2">Event Details</h3>
          <div>
            <label className="block text-sm text-neutral-400 mb-1">Event Name *</label>
            <input required value={form.event_name} onChange={e => setForm({...form, event_name: e.target.value})}
              className="w-full px-4 py-2 rounded-lg bg-neutral-700 text-white border border-neutral-600 focus:border-accent outline-none" data-testid="admin-event-name" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-neutral-400 mb-1">Category *</label>
              <select required value={form.category} onChange={e => setForm({...form, category: e.target.value})}
                className="w-full px-4 py-2 rounded-lg bg-neutral-700 text-white border border-neutral-600 focus:border-accent outline-none" data-testid="admin-event-category">
                <option value="">Select category</option>
                {eventCategories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm text-neutral-400 mb-1">Organizer</label>
              <input value={form.organizer} onChange={e => setForm({...form, organizer: e.target.value})}
                placeholder="Explore MHC" className="w-full px-4 py-2 rounded-lg bg-neutral-700 text-white border border-neutral-600 focus:border-accent outline-none" />
            </div>
          </div>
          <div>
            <label className="block text-sm text-neutral-400 mb-1">Description *</label>
            <textarea required value={form.description} onChange={e => setForm({...form, description: e.target.value})}
              className="w-full px-4 py-2 rounded-lg bg-neutral-700 text-white border border-neutral-600 focus:border-accent outline-none h-24 resize-none" data-testid="admin-event-desc" />
          </div>
        </div>

        <div className="bg-neutral-800 rounded-xl p-6 space-y-4">
          <h3 className="text-lg font-semibold text-white mb-2">Date, Time & Location</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-neutral-400 mb-1">Date *</label>
              <input type="date" required value={form.event_date} onChange={e => setForm({...form, event_date: e.target.value})}
                className="w-full px-4 py-2 rounded-lg bg-neutral-700 text-white border border-neutral-600 focus:border-accent outline-none" data-testid="admin-event-date" />
            </div>
            <div>
              <label className="block text-sm text-neutral-400 mb-1">Start Time *</label>
              <input required value={form.start_time} onChange={e => setForm({...form, start_time: e.target.value})}
                placeholder="10:00 AM" className="w-full px-4 py-2 rounded-lg bg-neutral-700 text-white border border-neutral-600 focus:border-accent outline-none" data-testid="admin-event-start" />
            </div>
            <div>
              <label className="block text-sm text-neutral-400 mb-1">End Time</label>
              <input value={form.end_time} onChange={e => setForm({...form, end_time: e.target.value})}
                placeholder="2:00 PM" className="w-full px-4 py-2 rounded-lg bg-neutral-700 text-white border border-neutral-600 focus:border-accent outline-none" />
            </div>
          </div>
          <div>
            <label className="block text-sm text-neutral-400 mb-1">Location *</label>
            <input required value={form.location} onChange={e => setForm({...form, location: e.target.value})}
              placeholder="Venue name, address" className="w-full px-4 py-2 rounded-lg bg-neutral-700 text-white border border-neutral-600 focus:border-accent outline-none" data-testid="admin-event-location" />
          </div>
        </div>

        <div className="bg-neutral-800 rounded-xl p-6 space-y-4">
          <h3 className="text-lg font-semibold text-white mb-2">Tickets & Image</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-neutral-400 mb-1">Ticket Price</label>
              <input value={form.ticket_price} onChange={e => setForm({...form, ticket_price: e.target.value})}
                placeholder="Free / $10" className="w-full px-4 py-2 rounded-lg bg-neutral-700 text-white border border-neutral-600 focus:border-accent outline-none" />
            </div>
            <div>
              <label className="block text-sm text-neutral-400 mb-1">Ticket URL</label>
              <input value={form.ticket_url} onChange={e => setForm({...form, ticket_url: e.target.value})}
                placeholder="https://" className="w-full px-4 py-2 rounded-lg bg-neutral-700 text-white border border-neutral-600 focus:border-accent outline-none" />
            </div>
          </div>
          <div>
            <label className="block text-sm text-neutral-400 mb-1">Event Image</label>
            {isEdit && editData?.image && (
              <div className="mb-2">
                <img src={`${window.location.origin}${editData.image}`} alt="" className="w-24 h-16 object-cover rounded-lg border border-neutral-600" />
                <p className="text-xs text-neutral-500 mt-1">Upload new image to replace</p>
              </div>
            )}
            <input type="file" accept="image/*" onChange={e => setImage(e.target.files[0] || null)}
              className="text-neutral-300" data-testid="admin-event-image" />
          </div>
        </div>

        <button type="submit" disabled={loading}
          className="px-8 py-3 bg-accent text-neutral-900 font-bold rounded-lg hover:bg-accent/90 transition-colors disabled:opacity-50"
          data-testid="admin-event-submit">
          {loading ? (isEdit ? 'Updating...' : 'Creating...') : (isEdit ? 'Update Event' : 'Create & Approve Event')}
        </button>
      </form>
    </div>
  );
};

export const AdminManageListings = ({ onRefresh }) => {
  const [allBusinesses, setAllBusinesses] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [search, setSearch] = useState('');
  const [listingType, setListingType] = useState('businesses');
  const [editingBusiness, setEditingBusiness] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [loadingData, setLoadingData] = useState(true);
  const [togglingId, setTogglingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const loadListings = async () => {
    setLoadingData(true);
    try {
      const [bizRes, evtRes] = await Promise.all([
        adminApi.getAllBusinesses(),
        adminApi.getAllEvents()
      ]);
      setAllBusinesses(bizRes.data);
      setAllEvents(evtRes.data);
    } catch (e) {
      console.error('Error loading listings:', e);
    } finally { setLoadingData(false); }
  };

  useEffect(() => { loadListings(); }, []);

  const handleEditDone = () => {
    setEditingBusiness(null);
    setEditingEvent(null);
    loadListings();
    if (onRefresh) onRefresh();
  };

  const handleToggleFeatured = async (type, id) => {
    setTogglingId(id);
    try {
      if (type === 'business') {
        const res = await adminApi.toggleFeaturedBusiness(id);
        toast.success(res.data.message);
      } else {
        const res = await adminApi.toggleFeaturedEvent(id);
        toast.success(res.data.message);
      }
      loadListings();
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error('Failed to toggle featured status');
    } finally { setTogglingId(null); }
  };

  const handleDelete = async (type, id, name) => {
    if (!window.confirm(`Are you sure you want to permanently delete "${name}"? This cannot be undone.`)) return;
    setDeletingId(id);
    try {
      if (type === 'business') {
        await adminApi.deleteBusiness(id);
      } else {
        await adminApi.deleteEvent(id);
      }
      toast.success(`${type === 'business' ? 'Business' : 'Event'} deleted`);
      loadListings();
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(`Failed to delete ${type}`);
    } finally { setDeletingId(null); }
  };

  if (editingBusiness) {
    return <AdminAddBusiness editData={editingBusiness} onSuccess={handleEditDone} onCancel={() => setEditingBusiness(null)} />;
  }
  if (editingEvent) {
    return <AdminAddEvent editData={editingEvent} onSuccess={handleEditDone} onCancel={() => setEditingEvent(null)} />;
  }

  const filteredBiz = allBusinesses.filter(b =>
    b.business_name?.toLowerCase().includes(search.toLowerCase()) ||
    b.primary_category?.toLowerCase().includes(search.toLowerCase())
  );
  const filteredEvt = allEvents.filter(e =>
    e.event_name?.toLowerCase().includes(search.toLowerCase()) ||
    e.category?.toLowerCase().includes(search.toLowerCase())
  );

  const statusBadge = (status) => {
    const colors = { approved: 'bg-green-500/20 text-green-400', pending: 'bg-amber-500/20 text-amber-400', rejected: 'bg-red-500/20 text-red-400' };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${colors[status] || 'bg-neutral-500/20 text-neutral-400'}`}>{status}</span>;
  };

  return (
    <div className="animate-fade-in" data-testid="admin-manage-listings">
      <h2 className="text-2xl font-bold text-white mb-6">Manage Listings</h2>

      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex gap-2">
          {['businesses', 'events'].map(t => (
            <button key={t} onClick={() => setListingType(t)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${listingType === t ? 'bg-accent text-neutral-900' : 'bg-neutral-700 text-neutral-300 hover:bg-neutral-600'}`}
              data-testid={`manage-tab-${t}`}>
              {t === 'businesses' ? `Businesses (${allBusinesses.length})` : `Events (${allEvents.length})`}
            </button>
          ))}
        </div>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search listings..."
            className="w-full pl-10 pr-4 py-2 rounded-lg bg-neutral-700 text-white border border-neutral-600 focus:border-accent outline-none"
            data-testid="manage-search" />
        </div>
      </div>

      {loadingData ? (
        <div className="text-center py-12 text-neutral-400">Loading...</div>
      ) : listingType === 'businesses' ? (
        <div className="bg-neutral-800 rounded-xl overflow-hidden">
          <table className="admin-table w-full">
            <thead>
              <tr>
                <th className="text-neutral-400">Name</th>
                <th className="text-neutral-400">Category</th>
                <th className="text-neutral-400">Status</th>
                <th className="text-neutral-400">Featured</th>
                <th className="text-neutral-400">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredBiz.map(b => (
                <tr key={b.id} className="text-white">
                  <td className="font-medium">{b.business_name}</td>
                  <td className="text-neutral-400 text-sm">{b.primary_category}</td>
                  <td>{statusBadge(b.status)}</td>
                  <td>
                    <button onClick={() => handleToggleFeatured('business', b.id)} disabled={togglingId === b.id}
                      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                        b.is_featured 
                          ? 'bg-accent/20 text-accent hover:bg-red-500/20 hover:text-red-400' 
                          : 'bg-neutral-700 text-neutral-400 hover:bg-accent/20 hover:text-accent'
                      } disabled:opacity-50`}
                      data-testid={`toggle-featured-biz-${b.id}`}>
                      <Star className={`w-3.5 h-3.5 ${b.is_featured ? 'fill-current' : ''}`} />
                      {togglingId === b.id ? '...' : b.is_featured ? 'Featured' : 'Not Featured'}
                    </button>
                  </td>
                  <td>
                    <div className="flex gap-2">
                      <button onClick={() => setEditingBusiness(b)}
                        className="p-2 rounded-lg bg-neutral-700 text-neutral-300 hover:bg-accent hover:text-neutral-900 transition-colors"
                        data-testid={`edit-biz-${b.id}`} title="Edit">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <a href={`/businesses/${b.slug || b.id}`} target="_blank" rel="noopener noreferrer"
                        className="p-2 rounded-lg bg-neutral-700 text-neutral-300 hover:bg-primary hover:text-white transition-colors" title="View">
                        <Eye className="w-4 h-4" />
                      </a>
                      <button onClick={() => handleDelete('business', b.id, b.business_name)}
                        disabled={deletingId === b.id}
                        className="p-2 rounded-lg bg-neutral-700 text-neutral-300 hover:bg-red-500 hover:text-white transition-colors disabled:opacity-50"
                        data-testid={`delete-biz-${b.id}`} title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredBiz.length === 0 && <div className="text-center py-8 text-neutral-500">No businesses found</div>}
        </div>
      ) : (
        <div className="bg-neutral-800 rounded-xl overflow-hidden">
          <table className="admin-table w-full">
            <thead>
              <tr>
                <th className="text-neutral-400">Name</th>
                <th className="text-neutral-400">Date</th>
                <th className="text-neutral-400">Category</th>
                <th className="text-neutral-400">Status</th>
                <th className="text-neutral-400">Featured</th>
                <th className="text-neutral-400">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredEvt.map(e => (
                <tr key={e.id} className="text-white">
                  <td className="font-medium">{e.event_name}</td>
                  <td className="text-neutral-400 text-sm">{e.event_date}</td>
                  <td className="text-neutral-400 text-sm">{e.category}</td>
                  <td>{statusBadge(e.status)}</td>
                  <td>
                    <button onClick={() => handleToggleFeatured('event', e.id)} disabled={togglingId === e.id}
                      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                        e.is_featured 
                          ? 'bg-accent/20 text-accent hover:bg-red-500/20 hover:text-red-400' 
                          : 'bg-neutral-700 text-neutral-400 hover:bg-accent/20 hover:text-accent'
                      } disabled:opacity-50`}
                      data-testid={`toggle-featured-evt-${e.id}`}>
                      <Star className={`w-3.5 h-3.5 ${e.is_featured ? 'fill-current' : ''}`} />
                      {togglingId === e.id ? '...' : e.is_featured ? 'Featured' : 'Not Featured'}
                    </button>
                  </td>
                  <td>
                    <div className="flex gap-2">
                      <button onClick={() => setEditingEvent(e)}
                        className="p-2 rounded-lg bg-neutral-700 text-neutral-300 hover:bg-accent hover:text-neutral-900 transition-colors"
                        data-testid={`edit-evt-${e.id}`} title="Edit">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <a href={`/events/${e.slug || e.id}`} target="_blank" rel="noopener noreferrer"
                        className="p-2 rounded-lg bg-neutral-700 text-neutral-300 hover:bg-primary hover:text-white transition-colors" title="View">
                        <Eye className="w-4 h-4" />
                      </a>
                      <button onClick={() => handleDelete('event', e.id, e.event_name)}
                        disabled={deletingId === e.id}
                        className="p-2 rounded-lg bg-neutral-700 text-neutral-300 hover:bg-red-500 hover:text-white transition-colors disabled:opacity-50"
                        data-testid={`delete-evt-${e.id}`} title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredEvt.length === 0 && <div className="text-center py-8 text-neutral-500">No events found</div>}
        </div>
      )}
    </div>
  );
};
